import { Response } from '../types/response';
import { CacheEntry, CacheConfig, CacheStorage, CacheRefreshCallback, CacheOptions } from './cache-types';

/**
 * Generic cache manager with TTL and background refresh capabilities
 */
export class CacheManager<T> {
  private storage: CacheStorage;
  private config: CacheConfig;
  private refreshCallback?: CacheRefreshCallback<T>;
  private refreshTimer?: number;
  private pendingRefreshes = new Set<string>();

  constructor(
    storage: CacheStorage,
    config: CacheConfig,
    refreshCallback?: CacheRefreshCallback<T>
  ) {
    this.storage = storage;
    this.config = config;
    this.refreshCallback = refreshCallback;
    
    if (this.refreshCallback && config.refreshInterval > 0) {
      this.startBackgroundRefresh();
    }
  }

  /**
   * Get cached data for a key
   */
  async get(key: string, options: CacheOptions = {}): Promise<T | null> {
    try {
      const entry = await this.storage.get<T>(key);
      
      if (!entry) {
        return null;
      }

      const now = Date.now();
      
      // Check if expired
      if (entry.expiresAt <= now) {
        await this.storage.remove(key);
        return null;
      }

      // Check if needs refresh (but still valid)
      if (!options.skipRefresh && this.shouldRefresh(entry, now)) {
        this.scheduleRefresh([key]);
      }

      return entry.data;
    } catch (error) {
      console.error(`Failed to get cache entry for key ${key}:`, error);
      return null;
    }
  }

  /**
   * Get multiple cached items
   */
  async getMultiple(keys: string[], options: CacheOptions = {}): Promise<Record<string, T>> {
    const results: Record<string, T> = {};
    const keysToRefresh: string[] = [];
    const now = Date.now();

    for (const key of keys) {
      try {
        const entry = await this.storage.get<T>(key);
        
        if (entry && entry.expiresAt > now) {
          results[key] = entry.data;
          
          if (!options.skipRefresh && this.shouldRefresh(entry, now)) {
            keysToRefresh.push(key);
          }
        }
      } catch (error) {
        console.error(`Failed to get cache entry for key ${key}:`, error);
      }
    }

    // Schedule refresh for items that need it
    if (keysToRefresh.length > 0) {
      this.scheduleRefresh(keysToRefresh);
    }

    return results;
  }

  /**
   * Set cached data for a key
   */
  async set(key: string, data: T, customTtl?: number): Promise<Response<void>> {
    try {
      const now = Date.now();
      const ttl = customTtl || this.config.ttl;
      
      const entry: CacheEntry<T> = {
        data,
        timestamp: now,
        expiresAt: now + ttl
      };

      await this.storage.set(key, entry);
      return undefined;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error occurred';
      return new Error(`Failed to set cache entry for key ${key}: ${message}`);
    }
  }

  /**
   * Set multiple cached items
   */
  async setMultiple(items: Record<string, T>, customTtl?: number): Promise<Response<void>> {
    try {
      const now = Date.now();
      const ttl = customTtl || this.config.ttl;
      
      for (const [key, data] of Object.entries(items)) {
        const entry: CacheEntry<T> = {
          data,
          timestamp: now,
          expiresAt: now + ttl
        };
        
        await this.storage.set(key, entry);
      }
      
      return undefined;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error occurred';
      return new Error(`Failed to set multiple cache entries: ${message}`);
    }
  }

  /**
   * Remove a cached item
   */
  async remove(key: string): Promise<Response<void>> {
    try {
      await this.storage.remove(key);
      return undefined;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error occurred';
      return new Error(`Failed to remove cache entry for key ${key}: ${message}`);
    }
  }

  /**
   * Clear all cached items
   */
  async clear(): Promise<Response<void>> {
    try {
      await this.storage.clear();
      return undefined;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error occurred';
      return new Error(`Failed to clear cache: ${message}`);
    }
  }

  /**
   * Get all cached keys
   */
  async getKeys(): Promise<string[]> {
    try {
      return await this.storage.getAllKeys();
    } catch (error) {
      console.error('Failed to get cache keys:', error);
      return [];
    }
  }

  /**
   * Clean expired entries
   */
  async cleanExpired(): Promise<Response<void>> {
    try {
      const keys = await this.storage.getAllKeys();
      const now = Date.now();
      
      for (const key of keys) {
        const entry = await this.storage.get<T>(key);
        if (entry && entry.expiresAt <= now) {
          await this.storage.remove(key);
        }
      }
      
      return undefined;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error occurred';
      return new Error(`Failed to clean expired entries: ${message}`);
    }
  }

  /**
   * Force refresh specific keys
   */
  async refresh(keys: string[]): Promise<Response<void>> {
    if (!this.refreshCallback || keys.length === 0) {
      return undefined;
    }

    try {
      // Filter out keys that are already being refreshed
      const keysToRefresh = keys.filter(key => !this.pendingRefreshes.has(key));
      if (keysToRefresh.length === 0) {
        return undefined;
      }

      // Mark keys as pending refresh
      keysToRefresh.forEach(key => this.pendingRefreshes.add(key));

      try {
        const refreshedData = await this.refreshCallback(keysToRefresh);
        
        // Update cache with refreshed data
        for (const [key, data] of Object.entries(refreshedData)) {
          if (data !== undefined) {
            await this.set(key, data);
          }
        }
        
        return undefined;
      } finally {
        // Remove keys from pending refresh
        keysToRefresh.forEach(key => this.pendingRefreshes.delete(key));
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error occurred';
      return new Error(`Failed to refresh cache entries: ${message}`);
    }
  }

  /**
   * Get cache statistics
   */
  async getStats(): Promise<{
    totalEntries: number;
    expiredEntries: number;
    validEntries: number;
    refreshableEntries: number;
  }> {
    try {
      const keys = await this.storage.getAllKeys();
      const now = Date.now();
      let totalEntries = 0;
      let expiredEntries = 0;
      let validEntries = 0;
      let refreshableEntries = 0;

      for (const key of keys) {
        const entry = await this.storage.get(key);
        if (entry) {
          totalEntries++;
          
          if (entry.expiresAt <= now) {
            expiredEntries++;
          } else {
            validEntries++;
            
            if (this.shouldRefresh(entry as CacheEntry<T>, now)) {
              refreshableEntries++;
            }
          }
        }
      }

      return {
        totalEntries,
        expiredEntries,
        validEntries,
        refreshableEntries
      };
    } catch (error) {
      console.error('Failed to get cache stats:', error);
      return {
        totalEntries: 0,
        expiredEntries: 0,
        validEntries: 0,
        refreshableEntries: 0
      };
    }
  }

  /**
   * Start background refresh timer
   */
  private startBackgroundRefresh(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
    }

    this.refreshTimer = window.setInterval(async () => {
      try {
        const keys = await this.getKeysNeedingRefresh();
        if (keys.length > 0) {
          await this.refresh(keys);
        }
      } catch (error) {
        console.error('Background refresh failed:', error);
      }
    }, this.config.refreshInterval);
  }

  /**
   * Stop background refresh timer
   */
  private stopBackgroundRefresh(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = undefined;
    }
  }

  /**
   * Check if an entry should be refreshed
   */
  private shouldRefresh(entry: CacheEntry<T>, currentTime: number): boolean {
    const timeUntilExpiry = entry.expiresAt - currentTime;
    return timeUntilExpiry <= this.config.refreshThreshold;
  }

  /**
   * Get keys that need refreshing
   */
  private async getKeysNeedingRefresh(): Promise<string[]> {
    try {
      const keys = await this.storage.getAllKeys();
      const now = Date.now();
      const keysToRefresh: string[] = [];

      for (const key of keys) {
        if (this.pendingRefreshes.has(key)) {
          continue;
        }
        
        const entry = await this.storage.get<T>(key);
        if (entry && entry.expiresAt > now && this.shouldRefresh(entry as CacheEntry<T>, now)) {
          keysToRefresh.push(key);
        }
      }

      return keysToRefresh;
    } catch (error) {
      console.error('Failed to get keys needing refresh:', error);
      return [];
    }
  }

  /**
   * Schedule refresh for specific keys (debounced)
   */
  private scheduleRefresh(keys: string[]): void {
    // Simple debouncing - refresh after a short delay
    setTimeout(() => {
      this.refresh(keys);
    }, 100);
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    this.stopBackgroundRefresh();
    this.pendingRefreshes.clear();
  }
}
